import { useMemo } from 'react';

import {
  buildBreedOptions,
  findBreedByToken,
  kindFromInput,
  useAnimatedTotals
} from '../hooks/useResultsPanel';

import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';
import type { CalculatorInput } from './resultsPanel.types';

export type ResultsPanelViewModelArgs = {
  inputs: Record<string, string | number | boolean | null>;
  breeds: CostCalculatorBreed[];
  calculatorInputs: CalculatorInput[];
  hasResults: boolean;
  resultsList: Array<{ id: string; value: number | null }>;
};

export const useResultsPanelViewModel = ({
  inputs,
  breeds,
  calculatorInputs,
  hasResults,
  resultsList
}: ResultsPanelViewModelArgs) => {
  const hasSelectedBreed = useMemo(() => {
    const raw = inputs.breed;
    return typeof raw === 'string' && raw.trim().length > 0;
  }, [inputs.breed]);

  const selectedBreed = useMemo(
    () => findBreedByToken(breeds, inputs.breed),
    [breeds, inputs.breed]
  );

  const breedOptions = useMemo(() => buildBreedOptions(breeds), [breeds]);

  const calculatorImageUrl =
    selectedBreed?.calculatorImageUrl ??
    selectedBreed?.photoUrl ??
    'https://paction-shared-resources.s3.ca-central-1.amazonaws.com/common/pup-calculator.webp';

  const resultsInputs = useMemo(() => {
    const age = (calculatorInputs ?? []).find(
      (input) => kindFromInput(input) === 'age'
    );
    const location = (calculatorInputs ?? []).find(
      (input) => kindFromInput(input) === 'location'
    );
    return [age, location].filter(Boolean) as typeof calculatorInputs;
  }, [calculatorInputs]);

  const displayedTotals = useAnimatedTotals(hasResults, resultsList);

  return {
    hasSelectedBreed,
    selectedBreed,
    breedOptions,
    calculatorImageUrl,
    resultsInputs,
    displayedTotals
  };
};
